/**
 * DIMENSION entity → `RenderableEntity` adapter (TYPED).
 *
 * DEFUSE Lane H / S8 — typed sibling of `../../dimensions/dimensionToPrimitives.ts`.
 * Reads the canonical `DimensionEntity` fields directly off the model
 * (`model.ts`: `dimensionEntitySchema` — `defPoint` / `textMidpoint` /
 * `extLine1Point` / `extLine2Point` / `thirdPoint` / `dimensionType` /
 * `measurement` / `dimensionText` / `textHeight` / `styleName` /
 * `annotativeContexts`) instead of the legacy `EntityPreview.properties`
 * string bag, then feeds the SAME shared builder
 * (`../geom/dimensionGeometry.ts`) to produce a byte-identical
 * `RenderableEntity` (golden-parity) for the same entity.
 *
 * # Field mapping (model → shared-builder raw fields)
 *
 *   - `entity.extLine1Point` / `extLine2Point` → `ext1` / `ext2`. Optional on
 *     the schema (some subtypes — Radius/Ordinate — never populate ext2); the
 *     legacy bag defaults an absent point to `{x:0,y:0}` (`ext1 = entity.
 *     extLine1Point ?? {x:0,y:0}` in `buildDimensionPreview`) — mirrored here.
 *   - `entity.defPoint` / `textMidpoint` → `dimLine`, via the SAME
 *     `defPt.x || textMid.x` fallback the bag producer applies (a defPoint
 *     coordinate of exactly 0 falls through to the text-midpoint twin).
 *   - `entity.thirdPoint` → `thirdPoint` (x/y only — z dropped, matching the
 *     bag's `"Third point X/Y"` pair, which never carried the 3rd-point z).
 *   - `entity.measurement` / `entity.dimensionText` / `entity.textHeight` →
 *     `measurement` / `textOverride` (`entity.dimensionText || "<>"`, the
 *     IDENTICAL expression the bag producer uses) / `textHeightRaw`.
 *   - `entity.dimensionType` → `subtype` via {@link resolveDimensionSubtype}
 *     (byte-identical to the bag round-trip `dimensionSubtypeLabel` →
 *     `parseDimensionSubtype`).
 *   - `entity.textAngleRelativeToDimLine` (DXF group 53, ezdxf's real
 *     `text_rotation`) → `textRotationOverride`, an ADDITIVE degrees offset
 *     the shared builder applies on top of the DIMTIH/dimline-derived base
 *     angle for every subtype. GitHub Codex review PR #558 (post-rebase
 *     fresh pass) P1 — previously nothing consumed a dimension-text-angle
 *     override at all (the field the MIRRTEXT reducer touched, `entity.
 *     textRotation` group 52, is ezdxf's `oblique` — a different, unrelated
 *     field). The LEGACY bag adapter (`dimensionToPrimitives.ts`) reads the
 *     matching `"Text angle relative to dim line"` / `"Text mirrored"`
 *     property-bag keys (`state/dxfGeometry.ts`'s `buildDimensionPreview`,
 *     added in the SAME round after a Codex finding that the PDF export
 *     path silently discarded the override) — golden parity holds.
 *   - `entity.textMirrored` → `textMirrored`, the handedness companion
 *     (see model.ts's doc for the DXF-persistence scope note — unlike its
 *     Multileader/ATTRIB siblings this is render/PDF-purpose only, no
 *     dxf-io import/export wiring).
 *
 * # DIMVAR (style) resolution — NOT `readDimStyle`'s typed branch
 *
 * See {@link resolveDimensionStyleRow} / {@link dimensionStyleRowToValues}'s
 * JSDoc in the shared geometry module: golden parity with the LIVE bag
 * render requires resolving DIMVARs from the named `DimensionStyle` TABLE
 * row (`ctx.dimensionStyles`), never from `entity.styleOverrides` (a
 * different, currently render-unwired channel).
 *
 * # DOCUMENTED PRE-EXISTING GAP — annotation-scale context switching is dead
 *
 * The bag path reads the active annotation scale from a property-bag key
 * (`__active_annotation_scale`) that NO producer ever writes (confirmed:
 * only a `command-engine` side-channel descriptor references the key name;
 * `buildDimensionPreview` never sets it) — so `annoScale` is always `null`
 * in the LIVE bag render and the annotative per-scale override
 * (`applyDimensionAnnotativeOverride`) is a permanent no-op today. This
 * typed adapter reproduces that exactly (passes `null`) rather than wiring
 * `ctx.annotationScale` — doing so would make an annotative DIMENSION under
 * a non-1:1 CANNOSCALE render DIFFERENTLY typed-vs-legacy, breaking the
 * golden-parity requirement this Lane's acceptance test enforces. Wiring a
 * real annotation scale into DIMENSION rendering is out of Lane H scope —
 * tracked as a follow-up (same class of gap as MLEADERSTYLE-table-unused,
 * see `multileaderFromEntity.ts`).
 */

import type { DimensionEntity, DimensionStyle } from "@dxf-ai-editor/drawing-model";

import type { RenderableEntity, Vec3 } from "../../types";

import {
  buildRenderableShell,
  resolveShellColorHex,
  type AdapterContext,
} from "../adapterContext";
import {
  applyDimensionAnnotativeOverride,
  buildDimensionGeometry,
  dimensionStyleRowToValues,
  finalizeDimensionBbox,
  resolveDimensionStyleRow,
  resolveDimensionSubtype,
  toDimensionSceneGeometry,
  type DimensionRawFields,
} from "../geom/dimensionGeometry";
import { applyOffset } from "../../coordinateOrigin";

const EMPTY_DIM_STYLES: ReadonlyMap<string, DimensionStyle> = new Map();

function vec3(x: number, y: number, z: number = 0): Vec3 {
  return { x, y, z };
}

/** Typed adapter for the `Dimension` entity (`dimensionEntitySchema`). */
export function dimensionFromEntity(
  entity: DimensionEntity,
  ctx: AdapterContext,
): RenderableEntity {
  const ext1 = entity.extLine1Point ?? { x: 0, y: 0 };
  const ext2 = entity.extLine2Point ?? { x: 0, y: 0 };
  const defPt = entity.defPoint;
  const textMid = entity.textMidpoint;
  const thirdPoint = entity.thirdPoint ?? { x: 0, y: 0 };

  const raw: DimensionRawFields = {
    ext1,
    ext2,
    dimLine: {
      x: defPt.x || textMid.x,
      y: defPt.y || textMid.y,
    },
    thirdPoint: { x: thirdPoint.x, y: thirdPoint.y },
    measurement: entity.measurement,
    textOverride: entity.dimensionText || "<>",
    textHeightRaw: entity.textHeight,
    // GitHub Codex review PR #558 P1 — DXF group 53 (ezdxf `text_rotation`),
    // additive to the DIMTIH/dimline-derived base angle. See
    // `DimensionRawFields.textRotationOverride`'s doc for the group-52-vs-53
    // rationale.
    textRotationOverride: entity.textAngleRelativeToDimLine,
    // GitHub Codex review PR #558 (post-rebase fresh pass) P1 — handedness
    // companion (see model.ts's `textMirrored` doc for the DXF-persistence
    // scope note).
    textMirrored: entity.textMirrored ?? false,
  };

  const dimStylesByName = ctx.dimensionStyles ?? EMPTY_DIM_STYLES;
  const styleRow = resolveDimensionStyleRow(
    entity.styleName ?? "Standard",
    dimStylesByName,
  );
  const baseStyle = dimensionStyleRowToValues(styleRow);
  const annotative = styleRow !== undefined && styleRow.DIMANNO !== 0;
  // `annoScale` is intentionally `null` — see the "DOCUMENTED PRE-EXISTING
  // GAP" module note above.
  const resolvedStyle = applyDimensionAnnotativeOverride(
    baseStyle,
    annotative,
    entity.annotativeContexts ?? [],
    null,
  );

  // Resolved color via shell builder (typed overload — no bag round-trip).
  const colorHex = resolveShellColorHex(entity, ctx);

  const subtype = resolveDimensionSubtype(entity.dimensionType);
  const result = buildDimensionGeometry(raw, subtype, resolvedStyle, colorHex);

  const fallback = applyOffset(vec3(raw.dimLine.x, raw.dimLine.y));
  const bbox = finalizeDimensionBbox(result.bbox, fallback);
  const geometry = toDimensionSceneGeometry(result.primitives);

  return buildRenderableShell(entity, ctx, geometry, bbox);
}
